import React, { useState, useEffect, useContext } from "react";
import { useLocation, useOutletContext, useNavigate } from "react-router-dom";
import { getQueryParams, useCurrentLocation } from "lib/utils";
import { useTranslation } from "react-i18next";
import FormContainer from "components/form-container";
import { Input } from "components/input";
import { Address } from "components/address";
import { useUpdateUser } from "api/use-user";
import LoadingSpinner from "components/loading-spinner";
import AuthContext from "contexts/AuthContext";

export default function DropoffSellerConfirmDetails() {
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const { t } = useTranslation();
  const { setSidebarVisual } = useOutletContext();
  const { user } = useContext(AuthContext);
  const { location: currentLocation } = useCurrentLocation();
  const navigate = useNavigate();

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: queryParams.firstName || user?.first_name || "",
    lastName: queryParams.lastName || user?.last_name || "",
    email: queryParams.email || user?.email || "",
  });

  // Address state (matching account-modal structure)
  const [address, setAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressCountry: "US",
    addressZip: "",
    addressState: "",
  });

  // Initialize address state when user changes
  useEffect(() => {
    if (user) {
      setAddress({
        addressLine1: user.street || user.address_line1 || "",
        addressLine2: user.address_line2 || "",
        addressCity: user.city || "",
        addressCountry: user.country || "US",
        addressZip: user.zip || user.postal_code || "",
        addressState: user.state || "",
      });
    }
  }, [user]);

  // Construct the existing address string for prefill
  const existingAddress =
    user?.street || user?.address_line1
      ? `${user.street || user.address_line1}, ${user.city}, ${user.state} ${user.zip || user.postal_code}`
      : "";

  // Store original values for change detection
  const [originalData, setOriginalData] = useState({
    firstName: "",
    lastName: "",
    address: {
      addressLine1: "",
      addressLine2: "",
      addressCity: "",
      addressCountry: "US",
      addressZip: "",
      addressState: "",
    },
  });

  // Update original data when user data loads
  useEffect(() => {
    if (user) {
      setOriginalData({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        address: {
          addressLine1: user.street || user.address_line1 || "",
          addressLine2: user.address_line2 || "",
          addressCity: user.city || "",
          addressCountry: user.country || "US",
          addressZip: user.zip || user.postal_code || "",
          addressState: user.state || "",
        },
      });
    }
  }, [user]);

  // Set sidebar visual
  useEffect(() => {
    setSidebarVisual("dollar");
  }, [setSidebarVisual]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isFormValid =
    formData.firstName &&
    formData.lastName &&
    formData.email &&
    address.addressLine1 &&
    address.addressCity &&
    address.addressCountry &&
    address.addressZip &&
    address.addressState;

  // Check if data has changed
  const hasChanges =
    formData.firstName !== originalData.firstName ||
    formData.lastName !== originalData.lastName ||
    address.addressLine1 !== originalData.address.addressLine1 ||
    address.addressLine2 !== originalData.address.addressLine2 ||
    address.addressCity !== originalData.address.addressCity ||
    address.addressCountry !== originalData.address.addressCountry ||
    address.addressZip !== originalData.address.addressZip ||
    address.addressState !== originalData.address.addressState;

  // Update user mutation
  const { mutate: updateUser } = useUpdateUser({
    onSuccess: () => {
      setIsSubmitting(false);
      // Navigate to next page with params
      const updatedParams = new URLSearchParams({
        ...queryParams,
        ...formData,
        address: `${address.addressLine1}${address.addressLine2 ? `, ${address.addressLine2}` : ""}, ${address.addressCity}, ${address.addressState} ${address.addressZip}`,
      });
      navigate(
        `/en-US/dropoff-seller/terms-conditions?${updatedParams.toString()}`
      );
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  // Handle form submission
  const handleSubmit = () => {
    if (!isFormValid || isSubmitting) return;

    // If no changes, proceed directly
    if (!hasChanges) {
      const updatedParams = new URLSearchParams({
        ...queryParams,
        ...formData,
        address: `${address.addressLine1}${address.addressLine2 ? `, ${address.addressLine2}` : ""}, ${address.addressCity}, ${address.addressState} ${address.addressZip}`,
      });
      navigate(
        `/en-US/dropoff-seller/terms-conditions?${updatedParams.toString()}`
      );
      return;
    }

    // Update backend if changes detected
    setIsSubmitting(true);

    const payload = {
      userEmail: user.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      addressCity: address.addressCity,
      addressCountry: address.addressCountry,
      addressZip: address.addressZip,
      addressState: address.addressState,
    };

    updateUser(payload);
  };

  return (
    <div
      className={`relative min-h-[calc(100vh-81px)] ${isSubmitting && "h-[calc(100vh-81px)] overflow-y-hidden"}`}
    >
      {isSubmitting && (
        <div className="absolute top-0 left-0 right-0 bottom-0 w-full h-full flex items-center justify-center bg-white z-20">
          <LoadingSpinner isLoading="Saving..." />
        </div>
      )}
      <FormContainer
        submitLabel={t("ui.button_continue")}
        onSubmit={handleSubmit}
        submitDisabled={!isFormValid || isSubmitting}
      >
        <div className="flex flex-col gap-regular">
          <h2 className="text-primary text-4xl md:text-6xl font-semibold">
            Hey, {formData.firstName || "there"}
          </h2>
          <p className="text-tertiary text-xl md:text-2xl">
            Please confirm your details.
          </p>
        </div>

        {/* Form */}
        <div className="w-full flex flex-col gap-large">
          <Input
            label="First name"
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
          />

          <Input
            label="Last name"
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
          />

          <Input
            label="Email address"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            disabled={true}
          />

          <Address
            address={address}
            setAddress={setAddress}
            currentLocation={currentLocation}
            existingAddress={existingAddress}
          />
        </div>
      </FormContainer>
    </div>
  );
}
